import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, Bell, KeyRound, LogOut, Menu, Settings, Sliders, UserRound } from "lucide-react";
import { ChangePasswordDialog } from "@/shared/auth/ChangePasswordDialog";
import { useUserAvatar } from "@/shared/auth/useUserAvatar";
import { useInAppNotificationsQuery } from "@/modules/sys/queries/useSysQueries";
import { OpsNotificationPopover } from "@/modules/sys";
import {
  bestMatchingNavPath,
  navLinkShouldEnd,
} from "@/app/nav/matchNavPath";
import type { OpsNavGroup } from "@/app/nav/types";
import { useOpsNav } from "@/app/nav/useOpsNav";
import { getScreenByPath, screenRegistry, type ScreenMeta } from "@/app/screenRegistry";
import {
  PageActionsProvider,
  usePageActionsState,
  usePageBreadcrumbTrailState,
  type PageBreadcrumbTrailItem,
} from "@/app/pageActions/PageActionsContext";
import { PageActionsToolbar } from "@/app/pageActions/PageActionsToolbar";
import { useAuth } from "@/shared/auth/AuthContext";
import { cn } from "@/shared/lib/cn";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";
import { setDocumentTitle } from "@/shared/lib/useDocumentTitle";
import { readReturnTo } from "@/shared/shell/returnTo";
import {
  Avatar,
  Banner,
  Breadcrumb,
  Button,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  WithTooltip,
} from "@/shared/ui";

const COLLAPSE_KEY = "galaxy.ops.sidebarCollapsed";
/** Match `--bp-web` / Tailwind `lg` — below this, use overlay nav (not icon rail). */
const DESKTOP_MQ = "(min-width: 1024px)";

/** Longest registered Ops list path that is a parent of `pathname` (detail / new / edit). */
function opsParentListPath(pathname: string): string | null {
  const listPaths = screenRegistry
    .filter((s) => s.shell === "ops" && s.path !== "/ops")
    .map((s) => s.path)
    .sort((a, b) => b.length - a.length);
  for (const p of listPaths) {
    if (pathname.startsWith(`${p}/`)) return p;
  }
  return null;
}

/** MX4 / notify inbox: peek host or inbox path from navigate state. */
function opsPeekReturnTo(state: unknown): string | null {
  return readReturnTo(state);
}

function resolveOpsScreen(pathname: string): ScreenMeta | undefined {
  const exact = getScreenByPath(pathname);
  if (exact) return exact;
  const parent = opsParentListPath(pathname);
  if (parent) return getScreenByPath(parent);
  return screenRegistry
    .filter((s) => s.shell === "ops" && s.path !== "/ops" && pathname.startsWith(s.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/** First path segment after a list route — entity key, or "Mới" for `/new`. */
function inferredDetailTrail(
  pathname: string,
  listPath: string,
): PageBreadcrumbTrailItem[] {
  if (!pathname.startsWith(`${listPath}/`)) return [];
  const first = pathname
    .slice(listPath.length + 1)
    .split("/")
    .filter(Boolean)[0];
  if (!first) return [];
  if (first === "new") return [{ label: "Mới" }];
  if (first === "edit") return [];
  try {
    return [{ label: decodeURIComponent(first) }];
  } catch {
    return [{ label: first }];
  }
}

function buildOpsCrumbs(
  pathname: string,
  screen: ScreenMeta | undefined,
  trailOverride: PageBreadcrumbTrailItem[],
  navGroups?: OpsNavGroup[],
) {
  const items: { label: string; to?: string | null }[] = [];
  const parentList = opsParentListPath(pathname);
  const trail =
    trailOverride.length > 0
      ? trailOverride
      : parentList
        ? inferredDetailTrail(pathname, parentList)
        : [];

  let menuGroupLabel = screen?.menuGroup;
  let screenLabel = screen?.label;

  if (navGroups && navGroups.length > 0) {
    const targetPath = screen?.path ?? pathname;
    for (const group of navGroups) {
      const match = group.children.find(
        (c) =>
          c.path === targetPath ||
          (screen && c.screenId === screen.screenId) ||
          (parentList && c.path === parentList),
      );
      if (match) {
        menuGroupLabel = group.label;
        if (screen && (match.path === screen.path || match.screenId === screen.screenId)) {
          screenLabel = match.label;
        }
        break;
      }
    }
  }

  if (menuGroupLabel) {
    items.push({ label: menuGroupLabel, to: null });
  } else {
    items.push({ label: "Ops", to: "/ops" });
  }
  if (screen && screen.path !== "/ops") {
    items.push({ label: screenLabel || screen.label, to: screen.path });
  } else if (!menuGroupLabel) {
    items.push({ label: "Tổng quan", to: "/ops" });
  }
  for (const t of trail) items.push(t);
  return items;
}

function navGroupTitle(index: number, label: string) {
  return `${index}. ${label}`;
}

function navItemTitle(groupIndex: number, itemIndex: number, label: string) {
  return `${groupIndex}.${itemIndex} ${label}`;
}

function OpsNavGroups({
  groups,
  collapsed,
  openGroupId,
  onToggleGroup,
  onNavigate,
}: {
  groups: OpsNavGroup[];
  collapsed: boolean;
  openGroupId: string | null;
  onToggleGroup: (id: string) => void;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const leafPaths = groups.flatMap((g) => g.children.map((c) => c.path));
  const activePath = bestMatchingNavPath(location.pathname, leafPaths);

  return (
    <nav className="scrollbar-none min-h-0 flex-1 overflow-y-auto px-2 py-3">
      <ul className="m-0 list-none space-y-1 p-0">
        {groups.map((group, groupIndex) => {
          const groupNo = groupIndex + 1;
          const groupTitle = navGroupTitle(groupNo, group.label);
          const Icon = group.icon;
          const groupActive = group.children.some((c) => c.path === activePath);
          const open = openGroupId === group.id;

          const triggerClass = cn(
            "relative flex w-full cursor-pointer touch-manipulation items-center gap-2 rounded-control px-2.5 py-2.5 text-body font-semibold",
            "min-h-[var(--touch-target-min)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
            groupActive
              ? "text-primary"
              : "text-text-secondary hover:bg-bg hover:text-text",
            collapsed && "justify-center px-0",
          );

          const iconEl = (
            <Icon
              className={cn(
                "size-4 shrink-0",
                groupActive ? "text-primary" : "text-text-muted",
              )}
              strokeWidth={groupActive ? 2.25 : 2} // VS3: Ops toolbar ≈ --icon-stroke-ops (2)
              aria-hidden
            />
          );

          if (collapsed) {
            return (
              <li key={group.id}>
                <DropdownMenu>
                  <WithTooltip label={groupTitle} side="right">
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={groupTitle}
                        className={triggerClass}
                      >
                        {iconEl}
                      </button>
                    </DropdownMenuTrigger>
                  </WithTooltip>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={8}
                    className="min-w-[12rem]"
                  >
                    <DropdownMenuLabel className="text-[11px] font-bold tracking-wide text-text-muted uppercase">
                      {groupTitle}
                    </DropdownMenuLabel>
                    {group.children.map((child, childIndex) => (
                      <DropdownMenuItem
                        key={child.path}
                        asChild
                        className="min-h-[var(--touch-target-min)] text-caption font-semibold"
                      >
                        <NavLink
                          to={child.path}
                          end={navLinkShouldEnd(child.path, leafPaths)}
                          onClick={() => onNavigate?.()}
                          className={cn(
                            child.path === activePath &&
                              "bg-primary-soft text-primary",
                          )}
                        >
                          {navItemTitle(groupNo, childIndex + 1, child.label)}
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          }

          return (
            <li key={group.id}>
              <button
                type="button"
                aria-label={groupTitle}
                aria-expanded={open}
                onClick={() => onToggleGroup(group.id)}
                className={triggerClass}
              >
                {iconEl}
                <span className="min-w-0 flex-1 truncate text-left">
                  {groupTitle}
                </span>
                <ChevronDown
                  className={cn(
                    "size-4 shrink-0 transition-transform duration-300 ease-out",
                    open && "rotate-180",
                    groupActive ? "text-primary" : "text-text-muted",
                  )}
                  aria-hidden
                />
              </button>
              {open ? (
                <ul className="relative m-0 mt-1 ml-4 list-none py-0.5 pl-0">
                    {group.children.map((child, index) => {
                      const isLast = index === group.children.length - 1;
                      return (
                        <li key={child.path} className="relative pl-4">
                          <span
                            aria-hidden
                            className={cn(
                              "pointer-events-none absolute left-0 border-l border-dashed border-border-strong",
                              isLast ? "top-0 h-1/2" : "inset-y-0",
                            )}
                          />
                          <span
                            aria-hidden
                            className="pointer-events-none absolute top-1/2 left-0 w-3 -translate-y-px border-t border-dashed border-border-strong"
                          />
                          <NavLink
                            to={child.path}
                            end={navLinkShouldEnd(child.path, leafPaths)}
                            onClick={() => onNavigate?.()}
                            className={() =>
                              cn(
                                "relative flex min-h-[var(--touch-target-min)] cursor-pointer touch-manipulation items-center rounded-control px-2.5 py-1.5 pr-3 text-caption font-semibold",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
                                child.path === activePath
                                  ? "bg-primary-soft text-primary"
                                  : "text-text-secondary hover:bg-bg hover:text-text",
                              )
                            }
                          >
                            {navItemTitle(groupNo, index + 1, child.label)}
                          </NavLink>
                        </li>
                      );
                    })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function OpsBrandHeader({ collapsed }: { collapsed: boolean }) {
  return (
    <Link
      to="/ops"
      aria-label="Về tổng quan"
      className={cn(
        "flex h-14 shrink-0 items-center border-b border-border px-3",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus-ring)]",
        collapsed ? "justify-center" : "gap-2.5 px-4",
      )}
    >
      <img
        src={collapsed ? "/logo/galaxy-icon.png" : "/logo/Logo.jpg"}
        alt="Galaxy One"
        className={cn(
          "shrink-0 object-contain object-center",
          collapsed ? "size-8" : "h-9 w-auto max-w-[8.5rem]",
        )}
      />
      {!collapsed ? (
        <div className="min-w-0 leading-tight">
          <p className="m-0 text-caption font-semibold tracking-wide text-text-secondary uppercase">
            Operations
          </p>
          <p className="m-0 truncate text-caption text-text-muted">Galaxy One</p>
        </div>
      ) : null}
    </Link>
  );
}

/** Logout + personal prefs pinned to sidebar / drawer footer. */
function OpsSidebarFooter({
  collapsed,
  onAfterLogout,
  bare = false,
}: {
  collapsed: boolean;
  onAfterLogout?: () => void;
  /** Skip outer border/padding when Drawer already wraps footer. */
  bare?: boolean;
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const notificationsQuery = useInAppNotificationsQuery({
    take: 200,
    unreadOnly: false,
    refetchIntervalMs: 45_000,
  });
  const unreadCount = useMemo(
    () => (notificationsQuery.data ?? []).filter((n) => !n.isRead).length,
    [notificationsQuery.data],
  );

  const handleLogout = () => {
    onAfterLogout?.();
    void logout();
  };

  const goNotifications = () => {
    onAfterLogout?.();
    void navigate("/ops/sys/notifications");
  };

  const control = collapsed ? (
    <div className="flex flex-col items-center gap-1">
      <span className="relative inline-flex">
        <IconButton
          label={unreadCount > 0 ? `Thông báo · ${unreadCount} chưa đọc` : "Thông báo của tôi"}
          icon={<Bell />}
          size="md"
          onClick={goNotifications}
        />
        {unreadCount > 0 ? (
          <span
            className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white shadow-xs"
            aria-hidden
          >
            {unreadCount > 99 ? "+99" : unreadCount}
          </span>
        ) : null}
      </span>
      <IconButton
        label="Thoát"
        icon={<LogOut />}
        size="md"
        className="hover:bg-[#C00000] hover:text-white"
        onClick={handleLogout}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="md"
        className="w-full justify-between gap-2 text-text-secondary"
        onClick={goNotifications}
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          <Bell className="size-4 shrink-0" aria-hidden />
          <span className="truncate">Thông báo của tôi</span>
        </span>
        {unreadCount > 0 ? (
          <span className="shrink-0 rounded-full bg-danger px-1.5 py-0.2 text-[10.5px] font-bold text-white tabular-nums font-mono">
            {unreadCount > 99 ? "+99" : unreadCount}
          </span>
        ) : null}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="md"
        className="w-full justify-start gap-2 text-text-secondary hover:bg-[#C00000] hover:text-white"
        onClick={handleLogout}
      >
        <LogOut className="size-4 shrink-0" aria-hidden />
        Thoát
      </Button>
    </div>
  );

  if (bare) {
    return <div className="w-full">{control}</div>;
  }

  return (
    <div
      className={cn(
        "mt-auto shrink-0 border-t border-border p-2",
        "pb-[max(0.5rem,env(safe-area-inset-bottom))]",
        collapsed ? "flex justify-center" : "px-3",
      )}
    >
      {control}
    </div>
  );
}

function OpsSidebar({
  groups,
  collapsed,
  openGroupId,
  onToggleGroup,
}: {
  groups: OpsNavGroup[];
  collapsed: boolean;
  openGroupId: string | null;
  onToggleGroup: (id: string) => void;
}) {
  return (
    <aside
      className={cn(
        "hidden h-full flex-col border-r border-border bg-surface lg:flex",
        collapsed ? "w-[4.5rem]" : "w-[var(--layout-sidebar-width)]",
      )}
      aria-label="Điều hướng chính"
    >
      <OpsBrandHeader collapsed={collapsed} />
      <OpsNavGroups
        groups={groups}
        collapsed={collapsed}
        openGroupId={openGroupId}
        onToggleGroup={onToggleGroup}
      />
    </aside>
  );
}

function OpsMobileNavDrawer({
  open,
  onOpenChange,
  groups,
  openGroupId,
  onToggleGroup,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: OpsNavGroup[];
  openGroupId: string | null;
  onToggleGroup: (id: string) => void;
}) {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Menu"
      description="Galaxy One · Operations"
      placement="left"
      bodyClassName="flex min-h-0 flex-col overflow-y-hidden p-0"
      className="max-w-[min(20rem,88vw)]"
      footer={
        <OpsSidebarFooter
          collapsed={false}
          onAfterLogout={() => onOpenChange(false)}
          bare
        />
      }
    >
      <OpsNavGroups
        groups={groups}
        collapsed={false}
        openGroupId={openGroupId}
        onToggleGroup={onToggleGroup}
        onNavigate={() => onOpenChange(false)}
      />
    </Drawer>
  );
}

function OpsTopbar({
  isDesktop,
  collapsed,
  onToggleCollapse,
  onOpenMobileNav,
}: {
  isDesktop: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenMobileNav: () => void;
}) {
  const { user, logout } = useAuth();
  const avatarUrl = useUserAvatar(user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const notificationsQuery = useInAppNotificationsQuery({
    take: 200,
    unreadOnly: false,
    refetchIntervalMs: 45_000,
  });
  const unreadCount = useMemo(
    () => (notificationsQuery.data ?? []).filter((n) => !n.isRead).length,
    [notificationsQuery.data],
  );
  const actions = usePageActionsState();
  const backAction = actions.find((a) => a.id === "back");
  const parentListPath = useMemo(
    () => opsParentListPath(location.pathname),
    [location.pathname],
  );
  const peekReturnTo = useMemo(
    () => opsPeekReturnTo(location.state),
    [location.state],
  );
  const showMobileBack =
    !isDesktop && Boolean(backAction || parentListPath || peekReturnTo);

  const handleMobileBack = () => {
    // MX4: peek full-page ← prefers host over list
    if (peekReturnTo) {
      void navigate(peekReturnTo);
      return;
    }
    if (backAction?.onClick) {
      backAction.onClick();
      return;
    }
    if (backAction?.to) {
      void navigate(backAction.to);
      return;
    }
    if (parentListPath) {
      void navigate(parentListPath);
    }
  };

  const handleLogout = () => {
    void logout();
  };

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-3 sm:px-4">
      {/* Top Left: Menu Toggle + Plant Info */}
      <div className="flex min-w-0 items-center gap-1">
        {/* MX3: phone keeps Menu + ← together so QD can jump modules without list-first */}
        {!isDesktop ? (
          <IconButton
            label="Mở menu"
            icon={<Menu />}
            size="md"
            onClick={onOpenMobileNav}
          />
        ) : (
          <IconButton
            label={collapsed ? "Mở sidebar" : "Thu gọn sidebar"}
            icon={<Menu />}
            size="md"
            onClick={onToggleCollapse}
          />
        )}
        {showMobileBack ? (
          <IconButton
            label="Quay lại"
            icon={<ChevronLeft />}
            size="md"
            onClick={handleMobileBack}
          />
        ) : null}
        <div className="min-w-0 leading-tight pl-1">
          <p className="m-0 truncate text-body font-semibold text-text">
            {user?.plantCode ?? "PLANT-01"}
          </p>
          <p className="m-0 truncate text-caption text-text-secondary">
            {user?.plantName || "Nhà máy vận hành"}
          </p>
        </div>
      </div>

      {/* Top Right: Notification Bell + Compact Avatar Dropdown */}
      <div className="flex items-center gap-1 sm:gap-2">
        <OpsNotificationPopover unreadCount={unreadCount} />

        <div className="h-4 w-px bg-border mx-0.5" />

        {/* User Account / Compact Avatar Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-control p-1 pr-1.5 text-left transition-colors hover:bg-bg cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
              aria-label="Tài khoản người dùng"
            >
              <Avatar
                src={avatarUrl}
                name={user?.fullName || user?.username}
                size="xs"
              />
              <div className="hidden sm:block min-w-0 leading-tight max-w-[10rem]">
                <div className="flex items-center gap-1">
                  <span className="truncate text-caption font-semibold text-text">
                    {user?.fullName || user?.username || "Người dùng"}
                  </span>
                  <ChevronDown className="size-3 text-text-muted shrink-0" />
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[15rem] p-1.5 shadow-lg">
            <div
              className="flex items-start gap-2.5 px-2.5 py-2 rounded-md hover:bg-bg/60 cursor-pointer transition-colors"
              onClick={() => navigate("/ops/sys/profile")}
              role="button"
              tabIndex={0}
            >
              <Avatar
                src={avatarUrl}
                name={user?.fullName || user?.username}
                size="xs"
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-caption font-bold text-text">
                  {user?.fullName || user?.username || "Người dùng"}
                </p>
                <p className="m-0 truncate text-[11px] text-text-muted">
                  {user?.email || (user?.username ? `@${user.username}` : "user")}
                </p>
                {user?.roles?.length ? (
                  <span className="mt-1 inline-block rounded bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    {user.roles.map((r) => r.name || r.code).join(", ")}
                  </span>
                ) : null}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-medium"
              onSelect={() => navigate("/ops/sys/profile")}
            >
              <UserRound className="size-4 text-text-muted" />
              <span>Thông tin tài khoản</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-medium"
              onSelect={() => navigate("/ops/sys/notifications")}
            >
              <Bell className="size-4 text-text-muted" />
              <div className="flex flex-1 items-center justify-between">
                <span>Thông báo của tôi</span>
                {unreadCount > 0 ? (
                  <span className="rounded-full bg-danger px-1.5 py-0.2 text-[10.5px] font-bold text-white tabular-nums font-mono">
                    {unreadCount > 99 ? "+99" : unreadCount}
                  </span>
                ) : null}
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-medium"
              onSelect={() => navigate("/ops/sys/notify-prefs")}
            >
              <Sliders className="size-4 text-text-muted" />
              <span>Cài đặt thông báo</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-medium"
              onSelect={() => navigate("/ops/sys/settings")}
            >
              <Settings className="size-4 text-text-muted" />
              <span>Cấu hình & Tùy chọn</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-medium"
              onSelect={() => setChangePasswordOpen(true)}
            >
              <KeyRound className="size-4 text-text-muted" />
              <span>Thay đổi mật khẩu</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              destructive
              className="cursor-pointer gap-2.5 px-2.5 py-2 text-caption font-semibold"
              onSelect={handleLogout}
            >
              <LogOut className="size-4 shrink-0" />
              <span>Đăng xuất</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </header>
  );
}

/** Ops shell — desktop sidebar + mobile left-nav drawer. */
export function OpsShell() {
  const location = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_MQ);
  const { groups: navGroups, errorBanner } = useOpsNav();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const activeGroupId = useMemo(() => {
    const activePath = bestMatchingNavPath(
      location.pathname,
      navGroups.flatMap((g) => g.children.map((c) => c.path)),
    );
    if (!activePath) return null;
    return (
      navGroups.find((g) => g.children.some((c) => c.path === activePath))
        ?.id ?? null
    );
  }, [navGroups, location.pathname]);

  const [openGroupId, setOpenGroupId] = useState<string | null>(activeGroupId);
  const [trackedActiveGroupId, setTrackedActiveGroupId] = useState(activeGroupId);
  // Sync before paint so reload does not flash closed, then animate open.
  if (trackedActiveGroupId !== activeGroupId) {
    setTrackedActiveGroupId(activeGroupId);
    if (activeGroupId) setOpenGroupId(activeGroupId);
  }

  useEffect(() => {
    if (!isDesktop) return;
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed, isDesktop]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isDesktop) setMobileNavOpen(false);
  }, [isDesktop]);

  /** Accordion: mở nhóm mới đóng nhóm cũ; click lại cùng nhóm thì đóng. */
  const toggleGroup = (id: string) =>
    setOpenGroupId((prev) => (prev === id ? null : id));

  return (
    <PageActionsProvider>
      <div className="app-frame flex h-svh min-h-0 bg-bg text-text">
        <OpsSidebar
          groups={navGroups}
          collapsed={collapsed}
          openGroupId={openGroupId}
          onToggleGroup={toggleGroup}
        />
        <OpsMobileNavDrawer
          open={!isDesktop && mobileNavOpen}
          onOpenChange={setMobileNavOpen}
          groups={navGroups}
          openGroupId={openGroupId}
          onToggleGroup={toggleGroup}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <OpsTopbar
            isDesktop={isDesktop}
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((v) => !v)}
            onOpenMobileNav={() => setMobileNavOpen(true)}
          />
          <div className="z-10 shrink-0 border-b border-border/80 bg-bg px-3 py-2 sm:px-4 sm:py-2.5">
            <OpsPageChrome navGroups={navGroups} />
          </div>
          <div className="scrollbar-none min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
            <div className="w-full max-w-full min-w-0 px-3 py-3 pb-24 sm:px-4 sm:py-4 lg:pb-4">
              {errorBanner ? (
                <Banner tone="warning" title={errorBanner} className="mb-3" />
              ) : null}
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </PageActionsProvider>
  );
}

function OpsPageChrome({ navGroups }: { navGroups?: OpsNavGroup[] }) {
  const location = useLocation();
  const screen = resolveOpsScreen(location.pathname);
  const trail = usePageBreadcrumbTrailState();
  const crumbs = useMemo(
    () => buildOpsCrumbs(location.pathname, screen, trail, navGroups),
    [location.pathname, screen, trail, navGroups],
  );

  useEffect(() => {
    const currentCrumb = crumbs[crumbs.length - 1]?.label;
    let title = currentCrumb;
    if (trail.length > 0 && screen?.label && currentCrumb !== screen.label) {
      title = `${currentCrumb} · ${screen.label}`;
    }
    setDocumentTitle(title || screen?.label || "Tổng quan");
  }, [crumbs, screen?.label, trail]);

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Breadcrumb items={crumbs} className="min-w-0 shrink-0" uppercase />
      <div className="flex w-full sm:w-auto items-center gap-2 sm:gap-3 flex-wrap">
        <div id="ops-breadcrumb-portal" className="flex w-full sm:w-auto items-center empty:hidden" />
        <PageActionsToolbar className="w-full sm:w-auto" />
      </div>
    </div>
  );
}
